import { memo, useRef, useState } from 'react';
import icons from '@icons';
import { Picture } from '@ui';
import { DigitalProductInfoBlock, Supplier } from '../components';
import ProductDocumentsModal from '@components/Modals/ProductDocumentsModal';
import './index.scss';
import scrollTo from '@utils/scrollTo';
import { twcls } from '@utils/twcls';
import { shownAttributes } from '@utils/jsonLd/product';

const {
  chevronRight: { black: crBlackOutlined, lightGrey: crLightGreyOutlined }
} = icons.Outline;

const ProductInfoTab = ({
  isBarMenu = false,
  defaultOpen,
  how_to_make,
  measureItem,
  weight_of_goods,
  country,
  attributes,
  productPackage,
  nutritional_value,
  energy_value,
  ingredients,
  storage_conditions,
  gost,
  documents,
  text,
  supplier_id,
  isDigital,
  carbs,
  reference_weight,
  proteins,
  fats
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!defaultOpen);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
  const visibleAttributes = shownAttributes(attributes);

  const firstItemRef = useRef(null);
  const scrollFunc = newValue => {
    if (firstItemRef?.current && newValue) {
      scrollTo.params({
        top: firstItemRef.current.offsetTop - 50,
        behavior: 'smooth'
      });
    }
  };

  const onClick = () => {
    const nextValue = !isCollapsed;
    scrollFunc(nextValue);
    setIsCollapsed(nextValue);
  };

  const productBGHZU = [
    {
      id: 0,
      title: 'Белки',
      value: proteins || '0'
    },
    {
      id: 1,
      title: 'Жиры',
      value: fats || '0'
    },
    {
      id: 2,
      title: 'Углеводы',
      value: carbs || '0'
    },
    {
      id: 3,
      title: 'кКал',
      value: energy_value || '0'
    }
  ].filter(item => !!item.value && item.value !== '0' && item.value !== '0.00');

  return (
    <div className='product-info-tab'>
      {/* БЖУ */}
      <div className='mb-4'>
        <p className='mb-2 text-[var(--dark-grey)] text-xs'>
          Пищевая ценность на {reference_weight || '100'} г
        </p>
        <ul className='grid w-full max-w-full grid-cols-4 gap-2 overflow-auto custom-scroll custom-scroll-mini'>
          {productBGHZU.map(item => (
            <li
              key={item.id}
              className='py-1.5 px-2 bg-[var(--ultra-light-grey)] w-full rounded-xl'
            >
              <p className='mb-1 text-base font-medium'>{item.value}</p>
              <p className='text-[var(--ultra-dark-grey)] text-xs'>
                {item.title}
              </p>
            </li>
          ))}
        </ul>
      </div>
      {how_to_make && (
        <div className='w-full p-3 mb-4 rounded-xl bg-[var(--light-grey)] '>
          <p className='mb-3 text-sm font-bold'>Способ приготовления</p>
          <div
            className='text-sm'
            dangerouslySetInnerHTML={{
              __html: how_to_make
            }}
          />
        </div>
      )}
      {ingredients && (
        <div className='mb-4'>
          <p className='mb-2 text-[var(--dark-grey)] text-xs'>Состав</p>
          <p className='text-sm'>{ingredients}</p>
        </div>
      )}
      {!!visibleAttributes.length && (
        <div className='mb-4' data-testid='product-attributes'>
          <p className='mb-2 text-[var(--dark-grey)] text-xs'>Характеристики</p>
          <dl className='flex flex-col gap-1 m-0 text-sm'>
            {visibleAttributes.map(item => (
              <div key={item.code} className='flex gap-2'>
                <dt className='shrink-0 max-w-[50%] break-words text-[var(--dark-grey)]'>
                  {item.title}
                </dt>
                <dd className='m-0 min-w-0 break-words'>{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div
        ref={firstItemRef}
        className={`product-info-tab__wrapper${
          !isCollapsed ? ' product-info-tab__wrapper--opened' : ''
        }`}
      >
        {storage_conditions && (
          <div className='mb-4'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>
              Срок годности, условия хранения
            </p>
            <p className='text-sm'>{storage_conditions}</p>
          </div>
        )}
        {country && (
          <div className='mb-4'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>Страна</p>
            <p className='text-sm'>{country}</p>
          </div>
        )}
        {productPackage && (
          <div className='mb-4'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>Тип упаковки</p>
            <p className='text-sm'>{productPackage}</p>
          </div>
        )}
        {measureItem && weight_of_goods && (
          <div className='mb-4'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>
              {measureItem.term || 'Вес'}
            </p>
            <p className='text-sm'>
              {weight_of_goods} {measureItem.symbol}
            </p>
          </div>
        )}
        {gost && (
          <div className='mb-4'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>ГОСТ/ТУ</p>
            <p className='text-sm'>{gost}</p>
          </div>
        )}
        {text && (
          <div className='mb-4 last-of-type:mb-0'>
            <p className='mb-2 text-[var(--dark-grey)] text-xs'>Описание</p>
            <div
              className={twcls(`
                text-sm
                leading-snug
                [&_p]:mb-1
                [&_p:last-of-type]:mb-0
                [&_a]:text-[var(--dark-red)]
                [&_a:hover]:text-[var(--main-red)]
                [&_h2]:text-lg
                [&_h2]:font-medium
                [&_h3]:font-medium
                [&_h3]:text-base
                [&_a]:underline
                [&_ul]:my-3
                [&_ul]:flex
                [&_ul]:flex-col
                [&_ul]:gap-1
                [&_ul]:list-disc
                [&_ul]:list-inside
                [&_ol]:list-decimal
                [&_ol]:list-inside
                [&_li]:list-[inherit]
                `)}
              dangerouslySetInnerHTML={{
                __html: text?.replace(/<p><\/p>/g, '')
              }}
            />
          </div>
        )}
        {!!documents?.length && (
          <button
            type='button'
            onClick={() => setIsDocumentsOpen(true)}
            className='flex items-center self-start p-0 mt-4 text-xs font-medium leading-tight bg-transparent border-0 appearance-none cursor-pointer text-[var(--dark-grey)] sm:mt-6'
          >
            Документы
            <Picture icon={crLightGreyOutlined} width='20' height='20' alt='' />
          </button>
        )}
      </div>
      {isDocumentsOpen && (
        <ProductDocumentsModal
          links={documents}
          onClose={() => setIsDocumentsOpen(false)}
        />
      )}

      {!defaultOpen && isCollapsed && (
        <div onClick={onClick} className={'product-info-tab__btn'}>
          Подробнее о товаре
          <Picture
            pictureClassName={`product-info-tab__btn__icon${
              !isCollapsed ? ' product-info-tab__btn__icon--opened' : ''
            }`}
            icon={crBlackOutlined}
            width='20'
            height='20'
          />
        </div>
      )}
      {!isBarMenu && (
        <div className='product-info-tab__supplier'>
          <Supplier supplier_id={supplier_id} />
        </div>
      )}
      {isDigital && (
        <div className='fromLg:hidden'>
          <DigitalProductInfoBlock />
        </div>
      )}
    </div>
  );
};

export default memo(ProductInfoTab);
